import { Link, useNavigate } from "react-router-dom";
import { LegalFooter } from "@/components/LegalFooter";
import { useState } from "react";
import { useAuth } from "@/lib/authContext";
import { Sparkles, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function LoginPage() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back!");
      nav("/app");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-zinc-50">
      {/* Left brand panel */}
      <div className="hidden md:flex flex-col justify-between bg-zinc-900 text-white p-12 relative overflow-hidden">
        <div className="absolute -top-32 -right-32 w-96 h-96 bg-blue-600/20 rounded-full blur-3xl" />
        <Link to="/" className="flex items-center gap-2 relative" data-testid="login-logo">
          <div className="w-8 h-8 bg-white rounded-md flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-zinc-900" strokeWidth={2.5} />
          </div>
          <span className="font-display font-black text-xl">NextGen<span className="text-blue-400"> Resolution Experts</span></span>
        </Link>
        <div className="relative">
          <h2 className="font-display font-black tracking-tighter text-5xl leading-[0.95] mb-6">
            Welcome back.<br />
            <span className="text-blue-400">Let's fix it.</span>
          </h2>
          <p className="text-zinc-400 max-w-md">
            Sign in to chat with your AI assistant, file a ticket, or browse the knowledge base.
          </p>
        </div>
        <div className="text-xs text-zinc-500 font-mono relative">Trusted by IT teams at 200+ companies</div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <Link to="/" className="md:hidden flex items-center gap-2 mb-12">
            <div className="w-8 h-8 bg-zinc-900 rounded-md flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-white" strokeWidth={2.5} />
            </div>
            <span className="font-display font-black text-xl">NextGen<span className="text-blue-600"> Resolution Experts</span></span>
          </Link>
          <div className="text-xs uppercase tracking-[0.3em] font-bold text-blue-600 mb-2">Sign in</div>
          <h1 className="font-display font-black tracking-tighter text-4xl mb-2">Good to see you.</h1>
          <p className="text-zinc-500 mb-10">Enter your work credentials to continue.</p>

          <form onSubmit={submit} className="space-y-5" data-testid="login-form">
            <div>
              <label className="text-xs font-bold tracking-widest uppercase text-zinc-500">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                className="mt-2 w-full rounded-xl border-2 border-zinc-200 bg-white px-4 py-3 text-sm focus:border-blue-600 focus:outline-none transition"
                data-testid="login-email"
              />
            </div>
            <div>
              <label className="text-xs font-bold tracking-widest uppercase text-zinc-500">Password</label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="mt-2 w-full rounded-xl border-2 border-zinc-200 bg-white px-4 py-3 text-sm focus:border-blue-600 focus:outline-none transition"
                data-testid="login-password"
              />
              <div className="mt-2 text-right">
                <Link to="/forgot-password" className="text-xs font-semibold text-blue-600 hover:underline" data-testid="login-forgot-password">
                  Forgot password?
                </Link>
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full inline-flex items-center justify-center gap-2 bg-zinc-900 text-white font-bold py-4 rounded-full hover:bg-zinc-800 disabled:opacity-50 transition-all hover:-translate-y-0.5"
              data-testid="login-submit"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Sign in <ArrowRight className="w-4 h-4" /></>}
            </button>
          </form>
          <div className="mt-8 text-sm text-zinc-500 text-center">
            New to NextGen Resolution Experts?{" "}
            <Link to="/signup" className="font-semibold text-blue-600 hover:underline" data-testid="login-to-signup">
              Create an account
            </Link>
          </div>
          <LegalFooter />
        </div>
      </div>
    </div>
  );
}
